import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import SEO, { SEO_CONFIG } from '@/components/SEO';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const LoginPage = ({ onLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      onLogin(response.data.token, response.data.user);
      
      toast.success('Giriş başarılı! Hoş geldiniz 🎉', {
        description: `Merhaba ${response.data.user.full_name || 'kullanıcı'}!`
      });
      
      navigate('/');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Giriş başarısız. E-posta veya şifre hatalı.';
      setError(errorMessage);
      toast.error('Giriş Başarısız', {
        description: errorMessage
      });
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100 px-4">
      <SEO {...SEO_CONFIG.login} />
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3">
          <div className="flex justify-center mb-4">
            <img 
              src="/icons/pilatika-logo.svg" 
              alt="Pilatika" 
              className="h-16 w-16"
              width="64"
              height="64"
            />
          </div>
          <h1 className="text-3xl font-bold text-center">Giriş Yap</h1>
          <p className="text-center text-gray-600">
            Pilatika platformuna hoş geldiniz
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">
                E-posta <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold">
                Şifre <span className="text-red-500">*</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="En az 6 karakter"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="h-11"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Giriş yapılıyor...
                </>
              ) : (
                <>🔐 Giriş Yap</>
              )}
            </Button>

            <div className="text-center text-sm text-gray-600">
              Hesabınız yok mu?{' '}
              <Link to="/register" className="text-purple-600 hover:text-purple-800 font-semibold">
                Kayıt Ol
              </Link>
            </div>
            
            <div className="text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
                ← Ana Sayfaya Dön
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
